import { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  Divider,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
  VolunteerActivism,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginSchema } from '@utils/validators';
import { useAuth } from '@hooks/useAuth';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signin, signinWithGoogle } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
  });

  // Get the intended destination (or default to dashboard)
  const from = location.state?.from?.pathname || '/dashboard';

  // Handle email/password login
  const onSubmit = async (data) => {
    try {
      setError('');
      setLoading(true);
      await signin(data.email, data.password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Sign-In
  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      const result = await signinWithGoogle();

      // If new user, redirect to registration page to complete profile
      if (result.isNewUser) {
        navigate('/register', {
          state: {
            googleUser: {
              uid: result.user.uid,
              email: result.user.email,
              displayName: result.user.displayName,
              photoURL: result.user.photoURL,
            },
          },
        });
      } else {
        // Existing user, go to dashboard
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Failed to sign in with Google.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ width: '100%' }}
    >
      {/* Branding */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: 'center', justifyContent: 'center', gap: { xs: 1, sm: 0 } }}>
        <VolunteerActivism sx={{ fontSize: { xs: 32, sm: 48 }, color: 'primary.main', mb: { xs: 0, sm: 0.5 } }} />
        <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
          <Typography variant={{ xs: 'h5', sm: 'h4' }} component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.4rem', sm: '2.125rem' } }}>
            ServeSync
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Sign in to your account
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Email Field */}
      <TextField
        fullWidth
        label="Email"
        type="email"
        margin="normal"
        autoComplete="email"
        inputMode="email"
        {...register('email')}
        error={!!errors.email}
        helperText={errors.email?.message}
        disabled={loading}
        inputProps={{ style: { fontSize: 16 } }}
        sx={{ mt: { xs: 1, sm: 2 }, mb: 0 }}
      />

      {/* Password Field */}
      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        margin="normal"
        autoComplete="current-password"
        {...register('password')}
        error={!!errors.password}
        helperText={errors.password?.message}
        disabled={loading}
        inputProps={{ style: { fontSize: 16 } }}
        sx={{ mt: 1, mb: 0 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                disabled={loading}
                sx={{ p: 1.5 }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Forgot Password Link */}
      <Box sx={{ mt: 0.5, mb: { xs: 1.5, sm: 2 } }}>
        <Link
          component={RouterLink}
          to="/forgot-password"
          variant="body2"
          underline="hover"
          sx={{ display: 'inline-block', py: 1, float: 'right' }}
        >
          Forgot password?
        </Link>
        <Box sx={{ clear: 'both' }} />
      </Box>

      {/* Sign In Button */}
      <Button
        fullWidth
        type="submit"
        variant="contained"
        size="large"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        sx={{ mb: { xs: 1.5, sm: 2 }, minHeight: 52, fontSize: '1rem' }}
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </Button>

      {/* Divider */}
      <Divider sx={{ my: { xs: 1.5, sm: 2 } }}>
        <Typography variant="body2" color="text.secondary">
          OR
        </Typography>
      </Divider>

      {/* Google Sign-In Button */}
      <Button
        fullWidth
        variant="outlined"
        size="large"
        startIcon={<GoogleIcon />}
        onClick={handleGoogleSignIn}
        disabled={loading}
        sx={{ mb: { xs: 2, sm: 3 }, minHeight: 52, fontSize: '1rem' }}
      >
        Sign in with Google
      </Button>

      {/* Sign Up Link */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Don't have an account?{' '}
          <Link
            component={RouterLink}
            to="/register"
            underline="hover"
            fontWeight="bold"
          >
            Sign up
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginForm;
